import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import { ShellLink } from "@/src/components";
import { Tooltip } from "antd";
import { cn } from "utils";
import log from "logbeacon";
import { ospThemes } from "@ospweb/sdk-ui";
import { Component, type ComponentType, type ReactNode, useMemo } from "react";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { SourceType } from "../components/Chat/Source";
import Quotes from "../components/markdownComponents/Quotes";
import { Iframe } from "../components/markdownComponents/Iframe";
import Code from "../components/markdownComponents/Code";
import { ArrayRenderer } from "../components/markdownComponents/ArrayRenderer"; // 已不再使用
import InlineAttachment from "../components/Chat/InlineAttachment";
import EdgenChart from "../components/Chat/Charts/EdgenChart";
import { ChatJsonRenderer } from "../components/Chat/jsonRender/ChatJsonRenderer";
import { type ChatJsonRenderContextValue } from "../components/Chat/jsonRender/ChatJsonRenderContext";
import { type DynamicUiItem } from "../components/Chat/jsonRender/parseDynamicUiBlocks";

interface DynamicUiFallbackBoundaryProps {
  fallbackText?: string;
  children: ReactNode;
}

interface DynamicUiFallbackBoundaryState {
  hasError: boolean;
}

class DynamicUiFallbackBoundary extends Component<
  DynamicUiFallbackBoundaryProps,
  DynamicUiFallbackBoundaryState
> {
  state: DynamicUiFallbackBoundaryState = { hasError: false };

  static getDerivedStateFromError(): DynamicUiFallbackBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(_: Error) {
    // log.error("[DynamicUI] Render error caught by fallback boundary", error);
  }

  render() {
    if (this.state.hasError && this.props.fallbackText) {
      return (
        <div className="body_output text-content_primary mt-2 mb-1 whitespace-pre-wrap break-words">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
          >
            {this.props.fallbackText}
          </ReactMarkdown>
        </div>
      );
    }

    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}

interface UseMarkdownComponentProps {
  source?: SourceType[];
  showSelect?: boolean;
  isExtension?: boolean;
  h3ClassName?: string;
  enableNavigation?: boolean;
  currentIndex?: number; // 添加currentIndex参数用于区分唯一性
  linkTarget?: "_self" | "_blank" | "_parent" | "_top"; // a 标签的 target 属性
  onLinkClick?: (
    href: string,
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => void; // 链接点击回调
  dynamicUiItems?: DynamicUiItem[];
  dynamicUiRenderContext?: ChatJsonRenderContextValue;
}

// 扩展 Components 类型以包含自定义组件
type CustomComponents = Components & {
  quotes: typeof Quotes;
  edgenchart: ComponentType<{
    node?: any;
    [key: string]: any;
  }>;
  "edgen-chart-render": ComponentType<{
    node?: any;
    [key: string]: any;
  }>;
  "chat-json-render": ComponentType<{
    node?: any;
    [key: string]: any;
  }>;
  "chart-loading": ComponentType<{
    node?: any;
    [key: string]: any;
  }>;
};

export const useMarkdownComponent = ({
  source,
  showSelect,
  isExtension,
  h3ClassName,
  enableNavigation = false,
  currentIndex = 0, // 添加currentIndex参数，默认值为0
  linkTarget = "_blank", // a 标签的 target 属性，默认为 _blank
  onLinkClick, // 链接点击回调
  dynamicUiItems,
  dynamicUiRenderContext,
}: UseMarkdownComponentProps) => {
  const components: Partial<CustomComponents> = useMemo(() => {
    return {
      code: ({ children }) => <Code>{children}</Code>,
      pre: ({ children }) => <>{children}</>,
      ol: ({ node, children, ...props }) => {
        return (
          <ol
            className="list-disc body_m list-outside ml-5 [&>li::marker]:text-content_brand_primary"
            {...props}
          >
            {children}
          </ol>
        );
      },
      li: ({ node, children, ...props }) => {
        return (
          <li className="body_output text-content_primary pb-2" {...props}>
            {children}
          </li>
        );
      },
      ul: ({ node, children, ...props }) => {
        return (
          <ul
            className="list-disc list-outside ml-5 [&>li::marker]:text-content_brand_primary"
            {...props}
          >
            {children}
          </ul>
        );
      },
      strong: ({ node, children, ...props }) => {
        return (
          <span className="font-semibold" {...props}>
            {children}
          </span>
        );
      },
      a: ({ node, children, ...props }) => {
        const { href = "", ...restProps } = props;

        // 如果提供了 onLinkClick 回调，拦截点击事件
        if (onLinkClick) {
          return (
            <ShellLink
              aria-label="use Markdown Component"
              data-openreplay-label={`use Markdown Component Link | ${href} | ${children}`}
              id="lnk-use-markdown-component"
              href={href}
              className="text-content_brand_primary"
              target={linkTarget}
              rel="noreferrer"
              onClick={(e) => {
                e.preventDefault();
                onLinkClick(href, e);
              }}
              {...restProps}
            >
              {children}
            </ShellLink>
          );
        }

        // 否则使用默认行为
        return (
          <ShellLink
            aria-label="use Markdown Component"
            data-openreplay-label={`use Markdown Component Link | ${href}`}
            id="lnk-use-markdown-component-2"
            href={href}
            className="text-content_brand_primary"
            target={linkTarget}
            rel="noreferrer"
            {...restProps}
          >
            {children}
          </ShellLink>
        );
      },
      h1: ({ node, children, ...props }) => {
        return (
          <h1
            aria-label="use Markdown Component h1"
            data-openreplay-label="use Markdown Component h1 Semantic"
            id="sem-use-markdown-component-h1"
            className="text-3xl headline_regular_m mt-6 mb-2 text-content_primary"
            {...props}
          >
            {children}
          </h1>
        );
      },
      h2: ({ node, children, ...props }) => {
        return (
          <h2
            aria-label="use Markdown Component h2"
            data-openreplay-label="use Markdown Component h2 Semantic"
            id="sem-use-markdown-component-h2"
            className="text-2xl headline_regular_s mt-6 mb-2 text-content_primary"
            {...props}
            {...(enableNavigation && {
              "data-markdown-heading": "true",
              "data-current-index": currentIndex, // 添加currentIndex数据属性
            })}
          >
            {children}
          </h2>
        );
      },
      h3: ({ node, children, ...props }) => {
        return (
          <h3
            aria-label="use Markdown Component h3"
            data-openreplay-label="use Markdown Component h3 Semantic"
            id="sem-use-markdown-component-h3"
            className={cn(
              "mt-6 mb-3 text-content_primary",
              h3ClassName ? h3ClassName : "title_xl",
            )}
            {...(enableNavigation && {
              "data-markdown-heading": "true",
              "data-current-index": currentIndex, // 添加currentIndex数据属性
            })}
            {...props}
          >
            {children}
          </h3>
        );
      },
      h4: ({ node, children, ...props }) => {
        return (
          <h4
            aria-label="use Markdown Component h4"
            data-openreplay-label="use Markdown Component h4 Semantic"
            id="sem-use-markdown-component-h4"
            className="text-lg title_l mt-6 mb-2 text-content_primary"
            {...props}
          >
            {children}
          </h4>
        );
      },
      h5: ({ node, children, ...props }) => {
        return (
          <h5
            aria-label="use Markdown Component h5"
            data-openreplay-label="use Markdown Component h5 Semantic"
            id="sem-use-markdown-component-h5"
            className="text-base body_medium_m mt-6 mb-2 text-content_primary"
            {...props}
          >
            {children}
          </h5>
        );
      },
      h6: ({ node, children, ...props }) => {
        return (
          <h6
            aria-label="use Markdown Component h6"
            data-openreplay-label="use Markdown Component h6 Semantic"
            id="sem-use-markdown-component-h6"
            className="text-sm body_medium_m mt-6 mb-2 text-content_primary"
            {...props}
          >
            {children}
          </h6>
        );
      },
      p: ({ node, children, ...props }) => {
        return (
          <p
            className="body_output text-content_primary mt-2 mb-1 whitespace-pre-wrap break-words"
            {...props}
          >
            {children}
          </p>
        );
      },
      quotes: ({ node, children, ...props }) => {
        return (
          <Quotes {...props} source={source}>
            {children}
          </Quotes>
        );
      },
      blockquote: ({ node, children, ...props }) => {
        return (
          <blockquote
            className="my-4 pl-4 border-l-4 border-border_solid text-content_secondary italic [&_*]:!text-content_secondary [&_*]:!text-inherit"
            {...props}
          >
            {children}
          </blockquote>
        );
      },
      hr: ({ node, children, ...props }) => {
        return <hr className="my-6 hidden border-border_solid" {...props} />;
      },
      table: ({ node, ...props }) => (
        <div className="markdown-table relative w-full my-6 overflow-hidden border border-background_white_overlay10 body_medium_m !font-normal [&_table_.quotes-container]:!hidden">
          <style
            dangerouslySetInnerHTML={{
              __html: `
                .no-scrollbar::-webkit-scrollbar {
                  display: none;
                }
              `,
            }}
          />
          <div
            className={cn(
              "overflow-x-auto w-full no-scrollbar",
              isExtension && "overflow-x-hidden",
            )}
          >
            <table
              className={cn(
                "min-w-full table-auto border-separate border-spacing-0 [&_tr>td:first-child]:sticky [&_tr>td:first-child]:left-0 [&_tr>td:first-child]:z-[1] [&_tr>td:first-child]:bg-background_solid_secondary [&_tr>td:first-child]:text-content_primary [&_tr>th:first-child]:sticky [&_tr>th:first-child]:left-0 [&_tr>th:first-child]:z-[1] [&_tr>th:first-child]:bg-background_solid_tertiary",
                {
                  "[&_tr>td]:group-hover/tableStyle:bg-background_solid_tertiary [&_tr>th:first-child]:group-hover/tableStyle:bg-background_solid_tertiary":
                    showSelect,
                },
              )}
              {...props}
            />
          </div>
        </div>
      ),
      tbody: ({ node, ...props }) => (
        <tbody className="font-normal" {...props} />
      ),
      thead: ({ node, ...props }) => (
        <thead className="font-normal" {...props} />
      ),
      th: ({ node, children, ...props }) => (
        <th
          className="body_medium_s text-left bg-background_solid_tertiary border-0 border-b border-background_white_overlay10 py-2 px-3 text-content_tertiary font-normal overflow-hidden text-ellipsis min-w-[120px]"
          {...props}
        >
          <div
            className={cn(
              "line-clamp-2 overflow-hidden text-ellipsis max-h-[74px]",
              isExtension && "max-w-full",
            )}
          >
            {children}
          </div>
        </th>
      ),
      tr: ({ node, ...props }) => (
        <tr
          className="text-left bg-transparent text-content_primary py-2 px-3"
          {...props}
        />
      ),
      td: ({ node, children, ...props }) => (
        <td
          className="border-0 border-b border-border_solid py-2 px-3 min-w-[120px]"
          {...props}
        >
          <div
            className={cn(
              "line-clamp-4 overflow-hidden text-ellipsis max-h-[112px]",
              isExtension && "max-w-full",
            )}
          >
            <Tooltip
              title={
                <span className="text-content_primary body_s">{children}</span>
              }
              color={ospThemes.colors.background_solid_tertiary}
              autoAdjustOverflow
              classNames={{
                body: "rounded-xl border border-background_white_overlay20 overflow-hidden",
              }}
            >
              <div className="body_medium_l">{children}</div>
            </Tooltip>
          </div>
        </td>
      ),
      iframe: ({ node, ...props }) => {
        const src = props.src || "";
        return <Iframe src={src} {...props} />;
      },
      array: ({ node }: { node: any; [key: string]: any }) => {
        try {
          // 递归提取所有type为text的内容
          const extractTextContent = (node: any): string => {
            if (node.type === "text") {
              return node.value;
            }
            if (node.children) {
              return node.children
                .map((child: any) => extractTextContent(child))
                .join("");
            }
            return "";
          };
          const textContent = extractTextContent(node);
          // 解析提取的内容为json
          // const data = JSON.parse(textContent);
          // if (!Array.isArray(data)) {
          //   log.debug("data is not an array", data);
          //   throw new Error('Data is not an array');
          // }
          return <ArrayRenderer>{textContent}</ArrayRenderer>;
        } catch (error) {
          log.error("[文案]array error", error);
          return (
            <span>
              {node.children.map((child: any) => child.value).join("")}
            </span>
          );
        }
      },
      edgenattachment: ({
        node,
        ...props
      }: {
        node?: any;
        [key: string]: any;
      }) => {
        return <InlineAttachment {...props} />;
      },
      edgenchart: ({ node, ...props }: { node?: any; [key: string]: any }) => {
        return <EdgenChart data={props["data"]} />;
      },
      "chat-json-render": ({
        node,
        ...props
      }: {
        node?: any;
        [key: string]: any;
      }) => {
        const index = Number(props["data-index"]);
        const item = dynamicUiItems?.[index];

        if (!item || item.kind !== "spec") {
          return null;
        }

        const itemRenderContext = {
          ...dynamicUiRenderContext,
          ...item.renderContext,
        };

        return (
          <DynamicUiFallbackBoundary
            fallbackText={item.renderContext.fallbackText}
          >
            <div className="my-3 w-full">
              <ChatJsonRenderer
                spec={item.spec}
                renderContext={itemRenderContext}
              />
            </div>
          </DynamicUiFallbackBoundary>
        );
      },
      "edgen-chart-render": ({
        node,
        ...props
      }: {
        node?: any;
        [key: string]: any;
      }) => {
        const index = Number(props["data-index"]);
        const item = dynamicUiItems?.[index];

        if (!item || item.kind !== "chart") {
          return null;
        }

        return (
          <DynamicUiFallbackBoundary
            fallbackText={item.renderContext.fallbackText}
          >
            <div className="my-3 w-full">
              <EdgenChart
                spec={item.spec}
                renderContext={{
                  ...dynamicUiRenderContext,
                  ...item.renderContext,
                }}
              />
            </div>
          </DynamicUiFallbackBoundary>
        );
      },
      "chart-loading": () => (
        <div className="my-3 w-full">
          <div
            style={{
              background: "rgba(1, 38, 43, 0.04)",
              borderRadius: 8,
              padding: "12px 14px",
              height: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "chart-loading-pulse 2s ease-in-out infinite",
            }}
          >
            <style>{`@keyframes chart-loading-pulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }`}</style>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              style={{ opacity: 0.3 }}
            >
              <rect
                x="2"
                y="12"
                width="4"
                height="10"
                rx="1"
                fill="currentColor"
              />
              <rect
                x="8"
                y="8"
                width="4"
                height="14"
                rx="1"
                fill="currentColor"
              />
              <rect
                x="14"
                y="4"
                width="4"
                height="18"
                rx="1"
                fill="currentColor"
              />
              <rect
                x="20"
                y="2"
                width="4"
                height="20"
                rx="1"
                fill="currentColor"
              />
            </svg>
          </div>
        </div>
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    source?.length,
    isExtension,
    showSelect,
    enableNavigation,
    currentIndex,
    linkTarget,
    onLinkClick,
    dynamicUiItems,
    dynamicUiRenderContext,
  ]);
  return components;
};
